/**
 * Toon House Analytics Events
 * 
 * This file documents all analytics events tracked in the Toon House Voting application.
 * It provides a centralized reference for event names, parameters, and their purposes.
 */

// Current Events Being Tracked:
// -----------------------------
// 1. character_selected - When a user selects a character
// 2. submit_button_clicked (active) - When the submit button is clicked with a selection
// 3. submit_button_clicked (inactive) - When the submit button is clicked without a selection
// 4. vote_submitted - When a vote is successfully submitted
// 5. vote_submission_failed - When a vote submission fails validation
// 6. page_view - When a page is viewed
// 7. form_interaction - When a user interacts with a form
// 8. cta_modal_shown - When the CTA modal is shown
// 9. cta_modal_closed - When the CTA modal is closed
// 10. cta_button_clicked - When a CTA button is clicked

/**
 * Helper function to log events with consistent formatting
 * @param {Object} client - Statsig client instance
 * @param {String} eventName - Name of the event
 * @param {String|Number} valueToLog - Primary value associated with the event
 * @param {Object} metadata - Additional metadata to log with the event
 */
export const logAnalyticsEvent = (client, eventName, valueToLog, metadata = {}) => {
  if (client) {
    // Add common metadata to all events
    const enrichedMetadata = {
      ...metadata,
      timestamp: new Date().toISOString(),
      session_id: getSessionId(),
      page_url: window.location.href,
    };
    
    client.logEvent(eventName, valueToLog, enrichedMetadata);
    console.log(`Analytics event logged: ${eventName}`, { valueToLog, metadata: enrichedMetadata });
  }
};

/**
 * Get or create a session ID for the current user session
 * @returns {String} Session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('toon_house_session_id');
  if (!sessionId) {
    sessionId = generateUniqueId();
    sessionStorage.setItem('toon_house_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Generate a unique ID for session tracking
 * @returns {String} Unique ID
 */
const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Proposed Additional Events to Track:
// -----------------------------------

/**
 * User Engagement Events
 */
export const EVENTS = {
  // Page View Events
  PAGE_VIEW: 'page_view',
  APP_LOADED: 'app_loaded',
  
  // Interaction Events
  CHARACTER_VIEWED: 'character_viewed',
  CHARACTER_SELECTED: 'character_selected',
  CHARACTER_DESELECTED: 'character_deselected',
  SUBMIT_CLICKED: 'submit_button_clicked',
  
  // Form Events
  FORM_FIELD_FOCUSED: 'form_field_focused',
  FORM_FIELD_FILLED: 'form_field_filled',
  FORM_FIELD_ERROR: 'form_field_error',
  FORM_SUBMITTED: 'form_submitted',
  FORM_INTERACTION: 'form_interaction',
  
  // Vote Events
  VOTE_SUBMITTED: 'vote_submitted',
  VOTE_SUBMISSION_FAILED: 'vote_submission_failed',
  
  // CTA Modal Events
  CTA_MODAL_SHOWN: 'cta_modal_shown',
  CTA_MODAL_CLOSED: 'cta_modal_closed',
  CTA_BUTTON_CLICKED: 'cta_button_clicked',
  
  // Timer Events
  COUNTDOWN_STARTED: 'countdown_started',
  COUNTDOWN_REACHED_HALFWAY: 'countdown_reached_halfway',
  COUNTDOWN_REACHED_FINAL_MINUTE: 'countdown_reached_final_minute',
  COUNTDOWN_EXPIRED: 'countdown_expired',
  
  // Error Events
  ERROR_OCCURRED: 'error_occurred',
  
  // Performance Events
  INITIAL_LOAD_TIME: 'initial_load_time',
  IMAGE_LOAD_ERROR: 'image_load_error'
};

export default {
  logAnalyticsEvent,
  EVENTS
};
